import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { captureException } from '@sentry/core'
import type { DateTime } from 'luxon'
import {
  createSection,
  createSectionWithProduct,
  getSection,
  getSectionsStreamForCollaborator,
  getSectionsStreamForInstructor,
  getSectionsStreamForInstructors,
  SectionPaymentMethod,
  SectionState,
} from '../firestore/Section'
import {
  type CompletionMetric,
  createDraftAssignment,
  createSectionAssignment,
  getAssignmentWithLatestExpiresAt,
  type FirestoreSectionAssignment,
} from '../firestore/SectionAssignment'
import { fetchOrganizationsByIds } from '../firestore/Organization'
import type { FirestoreSectionRequest } from '../firestore/SectionRequest'
import {
  createSectionInvoiceRequest,
  SectionRequestState,
  SectionRequestType,
} from '../firestore/SectionRequest'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { fetchSlides, getSlides } from '../firestore/Slide'
import { getSlideDeck } from '../firestore/SlideDeck'
import { getSlideDeckAuthors } from '../firestore/SlideDeckAuthor'
import { slideDeckAuthorSchema } from '../firestore/SlideDeckAuthor/schema'
import {
  fetchSlideDeckMaterialsForInstructor,
  getAllSlideDeckMaterialsForInstructor,
} from '../firestore/SlideDeckMaterial'
import {
  getSlideDeckReferences,
  schema as slideDeckReferenceSchema,
} from '../firestore/SlideDeckReference'
import {
  fetchSlideQuestions,
  getSlideQuestions,
} from '../firestore/SlideQuestion'
import { getSlideRubrics } from '../firestore/SlideRubric'
import { getSlideDeckRoles } from '../firestore/SlideDeckRole'
import { getTeachingPlan } from '../firestore/TeachingPlan'
import { getAssistedInstructorIDs } from '../firestore/UserProfile'
import { setPeerReviewQuestionsForAssignment } from '../firestore/SectionAssignmentPeerReviewQuestion'
import {
  applyApprovedFreeAdminPromotion,
  redeemPromotions,
} from '../firestore/UserPromotionRedemption'
import type {
  FirebaseRepository,
  RepositoryApiClient,
} from '../models/FirebaseRepository'
import { Section } from '../models/Section'
import {
  SectionAssignment,
  type AssignmentGroupingType,
  type AssignmentType,
} from '../models/SectionAssignment'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { SlideDeck } from '../models/SlideDeck'
import { SlideDeckAuthor } from '../models/SlideDeckAuthor'
import { SlideDeckMaterial } from '../models/SlideDeckMaterial'
import { SlideDeckReference } from '../models/SlideDeckReference'
import { SlideDeckRole } from '../models/SlideDeckRole'
import { SlideModel } from '../models/SlideModel'
import { SlideQuestion } from '../models/SlideQuestion'
import { SlideRubric } from '../models/SlideRubric'
import { TeachingPlan } from '../models/TeachingPlan'
import type { UserPromotion } from '../models/UserPromotion'
import { isMeetingQuestionScorable } from '../sources-of-truth/scoring'
import { sortQuestions } from '../sources-of-truth/sortQuestions'
import { sortRubrics } from '../sources-of-truth/sortRubrics'
import type { StaticModelCollection } from '../types'
import { calculateTimeLeftInSession } from '../util/time'
import { Cubit } from './core'
import { guardOnLoaded } from '../util/guardOn'
import { SectionSelectionCubit } from './SectionSelectionCubit'
import type { BreakoutUser } from '../models/BreakoutUser'
import type { SlideDeckApiInclude } from '../types/slideDeckAccess'
import {
  convertApiDocsToModels,
  populateModelsFromApiResponse,
} from '../firestore-mobx/apiModelHelper'

// 5 min: re-read the materials this long before their signed URLs expire, so
// a click right at the boundary still gets a working link.
const MATERIAL_URL_REFRESH_MARGIN_MS = 5 * 60 * 1000
// 1 min: the wait before retrying a failed refresh, and the shortest gap
// between two refreshes. A client clock that is hours off makes fresh links
// look expired already; without the gap the cubit would re-fetch nonstop.
const MATERIAL_URL_REFRESH_MIN_DELAY_MS = 60 * 1000

export class InstructorSlideDeckCubit extends Cubit {
  repository: FirebaseRepository
  forceStreamInsteadOfFetch: boolean
  impersonatedBreakoutUser: BreakoutUser

  slideDeckId: string

  assignmentWithLatestExpiresAt: SectionAssignment
  slideDeck: SlideDeck
  sectionPassPricing: SettingsSectionPassPricing
  slides: StaticModelCollection<SlideModel>
  questions: StaticModelCollection<SlideQuestion>
  materials: StaticModelCollection<SlideDeckMaterial>
  authors: StaticModelCollection<SlideDeckAuthor>
  rubrics: StaticModelCollection<SlideRubric>
  references: StaticModelCollection<SlideDeckReference>
  roles: StaticModelCollection<SlideDeckRole>
  sectionsFromBeingInstructor: StaticModelCollection<Section>
  sectionsFromBeingCollaborator: StaticModelCollection<Section>
  sectionsFromUsersIAssist: StaticModelCollection<Section>
  teachingPlan: TeachingPlan
  section: Section

  instructorUserId?: string
  catalogId?: string
  teachingPlanId?: string
  assignmentInfo?: { sectionId: string; assignmentId: string }
  materialUrlRefreshTimer?: ReturnType<typeof setTimeout>

  @observable
  sectionId?: string

  sectionSelectionCubit: SectionSelectionCubit

  constructor(
    repository: FirebaseRepository,
    slideDeckId: string,
    impersonatedBreakoutUser: BreakoutUser,
    options?: {
      instructorUserId?: string
      catalogId?: string
      teachingPlanId?: string
      sectionId?: string
      forceStreamInsteadOfFetch?: boolean
      // The assignment whose Course Materials tab shows this deck. With the v2
      // deck source on, the deck content is read from Adonis, which
      // authorizes the instructor through this assignment.
      assignmentInfo?: { sectionId: string; assignmentId: string }
    }
  ) {
    super()
    this.slideDeckId = slideDeckId
    this.instructorUserId = options?.instructorUserId
    this.catalogId = options?.catalogId
    this.sectionId = options?.sectionId
    this.teachingPlanId = options?.teachingPlanId
    this.forceStreamInsteadOfFetch = options?.forceStreamInsteadOfFetch ?? false
    this.assignmentInfo = options?.assignmentInfo
    this.impersonatedBreakoutUser = impersonatedBreakoutUser

    makeObservable(this)

    this.repository = repository
    this.slideDeck = SlideDeck.empty(repository)
    this.slides = SlideModel.emptyCollection(repository)
    this.questions = SlideQuestion.emptyCollection(repository)
    this.materials = SlideDeckMaterial.emptyCollection(repository)
    this.authors = SlideDeckAuthor.emptyCollection(repository)
    this.rubrics = SlideRubric.emptyCollection(repository)
    this.references = SlideDeckReference.emptyCollection(repository)
    this.roles = SlideDeckRole.emptyCollection(repository)
    this.sectionsFromBeingInstructor = Section.emptyCollection(repository)
    this.sectionsFromBeingCollaborator = Section.emptyCollection(repository)
    this.sectionsFromUsersIAssist = Section.emptyCollection(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
    this.teachingPlan = TeachingPlan.empty(repository)
    this.section = Section.empty(repository)
    this.assignmentWithLatestExpiresAt = SectionAssignment.empty(repository)

    this.sectionSelectionCubit = new SectionSelectionCubit(
      this.repository,
      impersonatedBreakoutUser,
      {
        catalogId: this.catalogId,
      }
    )
  }

  initialize(): void {
    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (pricing) => {
        this.sectionPassPricing.replaceModel(pricing)
      }
    )

    // v2: the Course Materials tab reads the deck document and its
    // subcollections from Adonis/Postgres in one call instead of the Firestore
    // reads in startSlideDeckReads. One-shot, like the assignment page's own v2
    // read: deck edits show after a reload.
    const api = this.repository.readsSlideDeckFromV2
      ? this.repository.api
      : undefined
    if (this.assignmentInfo && api) {
      void this.fetchSlideDeckViaApi(api, this.assignmentInfo)
    } else {
      this.startSlideDeckReads()
    }

    if (this.teachingPlanId && this.catalogId) {
      this.addStream(
        getTeachingPlan(this.repository, {
          catalogId: this.catalogId,
          teachingPlanId: this.teachingPlanId,
        }),
        (teachingPlan) => {
          this.teachingPlan.replaceModel(teachingPlan)
        }
      )
    }

    this.restartSectionStream()

    this.addStream(
      getSectionsStreamForCollaborator(this.repository, this.repository.uid),
      (sections) => {
        this.sectionsFromBeingCollaborator.replaceModels(
          sections.filter(
            (section) => section.data.sectionState !== SectionState.completed
          )
        )
      }
    )

    this.addStream(
      getAssistedInstructorIDs(
        this.repository,
        this.impersonatedBreakoutUser.uid
      ),
      (instructorIds) => {
        const sectionsStreamLabel = 'ta-sections-stream'
        this.removeStream(sectionsStreamLabel)

        if (instructorIds.length === 0) {
          this.sectionsFromUsersIAssist.replaceModels([])
          return
        }

        this.addStream(
          getSectionsStreamForInstructors(this.repository, instructorIds),
          (sections) => {
            this.sectionsFromUsersIAssist.replaceModels(
              sections.filter(
                (section) =>
                  section.data.sectionState !== SectionState.completed
              )
            )
          },
          { name: sectionsStreamLabel }
        )
      }
    )

    this.addStream(
      getSectionsStreamForInstructor(this.repository, {
        userId: this.instructorUserId ?? this.impersonatedBreakoutUser.uid,
      }),
      (sections) => {
        // filter our closed sections
        sections = sections.filter(
          (section) => section.data.sectionState !== SectionState.completed
        )
        this.sectionsFromBeingInstructor.replaceModels(sections)
      }
    )
  }

  startSlideDeckReads() {
    this.addStream(
      getSlideDeck(this.repository, { slideDeckId: this.slideDeckId }),
      (slideDeck) => {
        this.slideDeck.replaceModel(slideDeck)
      }
    )
    this.addStream(
      getSlideDeckAuthors(this.repository, { slideDeckId: this.slideDeckId }),
      (authors) => {
        this.authors.replaceModels(authors)
      }
    )

    this.addStream(
      getSlideRubrics(this.repository, { slideDeckId: this.slideDeckId }),
      (rubrics) => {
        this.rubrics.replaceModels(rubrics)
      }
    )

    this.addStream(
      getSlideDeckReferences(this.repository, {
        slideDeckId: this.slideDeckId,
      }),
      (references) => {
        this.references.replaceModels(references)
      }
    )

    this.addStream(
      getSlideDeckRoles(this.repository, { slideDeckId: this.slideDeckId }),
      (roles) => {
        this.roles.replaceModels(roles)
      }
    )

    this.forceStreamInsteadOfFetch
      ? this.setupQuestionStream()
      : this.fetchQuestions()
    this.forceStreamInsteadOfFetch
      ? this.setupMaterialsStream()
      : this.fetchMaterials()
    this.forceStreamInsteadOfFetch
      ? this.setupSlidesStream()
      : this.fetchSlides()
  }

  // Fills the same collections startSlideDeckReads does. `instructor: true`
  // returns the instructor material set (fetchSlideDeckMaterialsForInstructor's
  // rules). On failure every collection is marked loaded and empty so the tab
  // doesn't spin forever. A thrown error (network) is retried once, since
  // nothing else re-triggers this read for the cubit's lifetime.
  async fetchSlideDeckViaApi(
    api: RepositoryApiClient,
    assignmentInfo: { sectionId: string; assignmentId: string },
    attempt = 1
  ): Promise<void> {
    const include: SlideDeckApiInclude[] = [
      'slides',
      'questions',
      'rubrics',
      'roles',
      'materials',
      'authors',
      'references',
    ]

    const emptyAll = () => {
      this.slideDeck.replaceModel(SlideDeck.empty(this.repository))
      this.slides.replaceModels([])
      this.questions.replaceModels([])
      this.rubrics.replaceModels([])
      this.roles.replaceModels([])
      this.materials.replaceModels([])
      this.authors.replaceModels([])
      this.references.replaceModels([])
    }

    try {
      const result = await api.v1.slideDecks.show({
        params: { id: this.slideDeckId },
        query: {
          sectionId: assignmentInfo.sectionId,
          assignmentId: assignmentInfo.assignmentId,
          include,
          instructor: true,
        },
      })

      // The tab unmounted while the request was in flight.
      if (this.isDisposed) return

      if (!result.success) {
        captureException(
          new Error(`course materials v2 read failed: ${result.error}`),
          {
            tags: { feature: 'slide-deck-api', errorCode: result.errorCode },
            extra: { ...assignmentInfo, slideDeckId: this.slideDeckId },
          }
        )
        runInAction(emptyAll)
        return
      }

      // Validates the deck doc against the shared schema; throws (and lands in
      // the catch below) if the response is missing or malformed.
      const deckModel = SlideDeck.buildModelFromApiResponse(
        this.repository,
        result.slideDeck
      )
      // Authors and references arrive with Firestore timestamps
      // ({ seconds, nanoseconds }); parsing turns them into Dates, which
      // SlideDeckReference.getFormattedPublishedAt needs.
      const authors = convertApiDocsToModels(
        this.repository,
        (result.authors ?? []).map((doc) => ({
          id: doc.id,
          data: slideDeckAuthorSchema.parse(doc.data),
        })),
        SlideDeckAuthor
      )
      const references = convertApiDocsToModels(
        this.repository,
        (result.references ?? []).map((doc) => ({
          id: doc.id,
          data: slideDeckReferenceSchema.parse(doc.data),
        })),
        SlideDeckReference
      )

      runInAction(() => {
        this.slideDeck.replaceModel(deckModel)
        this.authors.replaceModels(authors)
        this.references.replaceModels(references)
        populateModelsFromApiResponse(this.repository, result, [
          { key: 'slides', modelClass: SlideModel, target: this.slides },
          {
            key: 'questions',
            modelClass: SlideQuestion,
            target: this.questions,
          },
          { key: 'rubrics', modelClass: SlideRubric, target: this.rubrics },
          { key: 'roles', modelClass: SlideDeckRole, target: this.roles },
          {
            key: 'materials',
            modelClass: SlideDeckMaterial,
            target: this.materials,
          },
        ])
      })
      this.scheduleMaterialUrlRefresh(api, assignmentInfo)
    } catch (error) {
      if (this.isDisposed) return
      if (attempt === 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        if (this.isDisposed) return
        return this.fetchSlideDeckViaApi(api, assignmentInfo, attempt + 1)
      }
      captureException(error, { tags: { feature: 'slide-deck-api' } })
      runInAction(emptyAll)
    }
  }

  // v2 material links are signed URLs that expire (6h on the server), and the
  // tab can stay open longer than that. Re-read the materials shortly before
  // the earliest one expires so every link keeps working. A timer that comes
  // due while the laptop sleeps fires on wake.
  scheduleMaterialUrlRefresh(
    api: RepositoryApiClient,
    assignmentInfo: { sectionId: string; assignmentId: string },
    minDelayMs = 0
  ) {
    clearTimeout(this.materialUrlRefreshTimer)

    const expiryTimes = this.materials.models
      .map((material) => material.signedUrlExpiresAt)
      .filter(
        (expiresAt): expiresAt is number =>
          expiresAt !== null && Number.isFinite(expiresAt)
      )
    if (expiryTimes.length === 0) return

    const refreshAt = Math.min(...expiryTimes) - MATERIAL_URL_REFRESH_MARGIN_MS
    this.materialUrlRefreshTimer = setTimeout(
      () => void this.refreshMaterialUrls(api, assignmentInfo),
      Math.max(minDelayMs, refreshAt - Date.now())
    )
  }

  // Tries again while the tab stays open, so a network blip at refresh time
  // doesn't leave the links to expire.
  retryMaterialUrlRefresh(
    api: RepositoryApiClient,
    assignmentInfo: { sectionId: string; assignmentId: string }
  ) {
    clearTimeout(this.materialUrlRefreshTimer)
    this.materialUrlRefreshTimer = setTimeout(
      () => void this.refreshMaterialUrls(api, assignmentInfo),
      MATERIAL_URL_REFRESH_MIN_DELAY_MS
    )
  }

  async refreshMaterialUrls(
    api: RepositoryApiClient,
    assignmentInfo: { sectionId: string; assignmentId: string }
  ) {
    try {
      const result = await api.v1.slideDecks.show({
        params: { id: this.slideDeckId },
        query: {
          sectionId: assignmentInfo.sectionId,
          assignmentId: assignmentInfo.assignmentId,
          include: ['materials'],
          instructor: true,
        },
      })
      if (this.isDisposed) return

      if (!result.success) {
        captureException(
          new Error(`course materials url refresh failed: ${result.error}`),
          {
            tags: { feature: 'slide-deck-api', errorCode: result.errorCode },
            extra: { ...assignmentInfo, slideDeckId: this.slideDeckId },
          }
        )
        // Only a server error can clear up on its own. NOT_FOUND, NOT_MEMBER
        // and ACCESS_DENIED stay that way, so a retry would only fail again.
        if (result.errorCode === 'INTERNAL_ERROR') {
          this.retryMaterialUrlRefresh(api, assignmentInfo)
        }
        return
      }

      runInAction(() => {
        populateModelsFromApiResponse(this.repository, result, [
          {
            key: 'materials',
            modelClass: SlideDeckMaterial,
            target: this.materials,
          },
        ])
      })
      this.scheduleMaterialUrlRefresh(
        api,
        assignmentInfo,
        MATERIAL_URL_REFRESH_MIN_DELAY_MS
      )
    } catch (error) {
      if (this.isDisposed) return
      captureException(error, { tags: { feature: 'slide-deck-api' } })
      this.retryMaterialUrlRefresh(api, assignmentInfo)
    }
  }

  dispose() {
    clearTimeout(this.materialUrlRefreshTimer)
    return super.dispose()
  }

  @action
  setSectionId(sectionId: string) {
    this.sectionId = sectionId
    this.restartSectionStream()
  }

  restartSectionStream() {
    this.removeStream('section')
    this.removeStream('latest-assignment')
    if (this.sectionId) {
      this.addStream(
        getSection(this.repository, {
          sectionId: this.sectionId,
        }),
        (section) => {
          this.section.replaceModel(section)
        },
        { name: 'section' }
      )

      this.addStream(
        getAssignmentWithLatestExpiresAt(this.repository, {
          sectionId: this.sectionId,
        }),
        (assignment) => {
          this.assignmentWithLatestExpiresAt.replaceModel(assignment)
        },
        { name: 'latest-assignment' }
      )
    } else {
      this.section.replaceModel(Section.empty(this.repository))
      this.assignmentWithLatestExpiresAt.replaceModel(
        SectionAssignment.empty(this.repository)
      )
    }
  }

  @computed
  get allowSectionPassPaymentMethod() {
    return (
      this.sectionPassPricing.hasData &&
      this.sectionPassPricing.data.sectionPassPrice > 0
    )
  }

  /**
   * Whether this deck has quiz questions that actually contribute to a
   * student's quiz score. Only multiple-choice questions on a slide count —
   * this mirrors `isMeetingQuestionScorable` / `calculateQuizScoreForUser`.
   *
   * Used to decide whether to prompt instructors for a quiz weight in the
   * grading setup modal. Checking merely "graded" questions (which includes
   * sorting questions, excluded from the quiz score) would show a quiz weight
   * that can never affect the grade. See BL-4663.
   */
  @computed
  get hasScorableQuizzes(): boolean {
    return this.questions.models.some((question) =>
      isMeetingQuestionScorable(question)
    )
  }

  @computed
  get questionsSorted() {
    const slideIds = this.slidesSorted.map((slide) => slide.id)

    return sortQuestions({
      questions: this.questions.models,
      slideIds,
    })
  }

  @computed
  get slidesSorted() {
    return this.slides.models.sort(
      (a, b) => a.data.slideOrder - b.data.slideOrder
    )
  }

  @computed
  get sortedRubrics() {
    return sortRubrics(this.rubrics.models, this.slides.models)
  }

  @computed
  get sortedSectionOptions() {
    // Returns a list of sections sorted by class and section name.
    // Returned in a format that can be used by the BreakoutSelect component.
    return guardOnLoaded(this.sections, (sections) => {
      return sections
        .map((section) => ({
          value: section.id,
          label: `${section.data.className}: ${section.data.sectionName}`,
          instructorUserId: section.data.instructorUserId,
          hasSectionDates:
            section.data.sectionStartDate && section.data.sectionEndDate
              ? true
              : false,
          sectionState: section.data.sectionState,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    })
  }

  @computed
  get sections() {
    return guardOnLoaded(
      [
        this.sectionsFromBeingInstructor,
        this.sectionsFromUsersIAssist,
        this.sectionsFromBeingCollaborator,
      ],
      (sections) => {
        const allSections = sections.flat()
        const uniqueSectionIds = new Set<string>()

        const uniqueSections = allSections.filter((section) => {
          if (uniqueSectionIds.has(section.id)) {
            return false
          }
          uniqueSectionIds.add(section.id)
          return true
        })

        return uniqueSections
      }
    )
  }

  @computed
  get estimatedExperienceDurationInMinutes(): number {
    const durationInMinutes = (slide: SlideModel) => {
      let duration = 0
      if (slide.data.slideVideoDuration) {
        duration = Math.ceil(slide.data.slideVideoDuration / 60)
      } else {
        duration = Math.ceil(slide.data.slideDuration / 60)
      }
      return duration
    }

    return calculateTimeLeftInSession({
      slides: this.slides._models.map((slide) => ({
        durationInMinutes: durationInMinutes(slide),
      })),
      roundToNearest: 5,
    })
  }

  async setupSlidesStream() {
    const stream = getSlides(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (slides) => {
      this.slides.replaceModels(slides)
    })
  }

  async fetchSlides() {
    try {
      const slides = await fetchSlides(this.repository, {
        slideDeckId: this.slideDeckId,
      })
      this.slides.replaceModels(slides)
    } catch (error) {
      captureException(error)
      this.slides.replaceModels([])
    }
  }

  async setupMaterialsStream() {
    const stream = getAllSlideDeckMaterialsForInstructor(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (materials) => {
      this.materials.replaceModels(materials)
    })
  }

  async fetchMaterials() {
    try {
      const data = await fetchSlideDeckMaterialsForInstructor(this.repository, {
        slideDeckId: this.slideDeckId,
      })
      this.materials.replaceModels(data)
    } catch (error) {
      captureException(error)
      this.materials.replaceModels([])
    }
  }

  async setupQuestionStream() {
    const stream = getSlideQuestions(this.repository, {
      slideDeckId: this.slideDeckId,
    })
    this.addStream(stream, (questions) => {
      this.questions.replaceModels(questions)
    })
  }

  async fetchQuestions() {
    try {
      const data = await fetchSlideQuestions(this.repository, {
        slideDeckId: this.slideDeckId,
      })

      this.questions.replaceModels(data)
    } catch (error) {
      captureException(error)
      this.questions.replaceModels([])
    }
  }

  async createDraftSectionAssignment(params: {
    sectionId: string
    assignmentType: AssignmentType
    slideDeckId: string
    assignedAt?: DateTime
    expiresAt?: DateTime
    assignmentOverdueAt?: DateTime
    catalogId?: string
    groupingType: AssignmentGroupingType
    groupingSize?: number
    groupingSizeMaximum?: number
    groupingSizeMinimum?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
    showFinalGradeToParticipants?: boolean
    gradeAppealsEnabled?: boolean
    completionMetric?: CompletionMetric
  }) {
    return createDraftAssignment(this.repository, { ...params })
  }

  async createSectionAssignment(params: {
    sectionId: string
    assignmentType: AssignmentType
    assignedAt: DateTime
    expiresAt: DateTime
    assignmentOverdueAt?: DateTime
    groupingType: AssignmentGroupingType
    slideDeckId: string
    groupingSizeMaximum?: number
    groupingSizeMinimum?: number
    catalogId?: string
    groupingSize?: number
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    customGroupingSelected?: boolean
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupScheduling?: boolean
    forbidStudentGroupSelection?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
    showFinalGradeToParticipants?: boolean
    gradeAppealsEnabled?: boolean
    completionMetric?: CompletionMetric
  }) {
    return createSectionAssignment(this.repository, { ...params })
  }

  async setPeerReviewQuestionsForDraftAssignment(params: {
    sectionId: string
    assignmentId: string
    questions: Array<{
      prQuestion: string
      prDescription: string
      prOrder: number
    }>
  }) {
    return setPeerReviewQuestionsForAssignment(this.repository, params)
  }

  createSectionInvoiceRequest = async ({
    sectionId,
    sectionRequest,
  }: {
    sectionId: string
    sectionRequest: Pick<
      FirestoreSectionRequest,
      | 'sectionRequestAssignmentCount'
      | 'sectionRequestStudentCount'
      | 'requestedAt'
      | 'organizationId'
      | 'sectionId'
      | 'updatedByUserId'
    >
  }) => {
    await createSectionInvoiceRequest(this.repository, sectionId, {
      ...sectionRequest,
      userId: this.repository.uid,
      sectionRequestType: SectionRequestType.invoice,
      sectionRequestState: SectionRequestState.pending,
      updatedAt: new Date(),
      requestedAt: new Date(),
    })
  }

  createSection = async ({
    className,
    sectionName,
    organizationId,
    sectionPaymentMethod,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    selectedProduct,
  }: {
    className: string
    sectionName: string
    organizationId?: string
    sectionPaymentMethod?: SectionPaymentMethod
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    selectedProduct?: { productId: string; isCustom: boolean }
  }) => {
    // If no payment method was explicitly set, check the organization's default
    let resolvedPaymentMethod = sectionPaymentMethod
    if (!resolvedPaymentMethod && organizationId) {
      const orgDocs = await fetchOrganizationsByIds(this.repository, [
        organizationId,
      ])
      const orgDoc = orgDocs[0]
      if (orgDoc?.exists()) {
        const orgData = orgDoc.data()
        if (orgData?.defaultCodelessAccess) {
          resolvedPaymentMethod = SectionPaymentMethod.sectionPassCodelessAccess
        }
      }
    }

    const baseParams = {
      className,
      sectionName,
      organizationId,
      userId: this.instructorUserId || this.repository.uid,
      sectionPaymentMethod: resolvedPaymentMethod ?? SectionPaymentMethod.token,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
    }

    if (selectedProduct) {
      const sectionId = await createSectionWithProduct(this.repository, {
        ...baseParams,
        currentUserId: this.repository.uid,
        instructorUserId: baseParams.userId,
        productId: selectedProduct.productId,
        customProduct: selectedProduct.isCustom,
      })

      return sectionId
    }

    const doc = await createSection(this.repository, baseParams)
    return doc.id
  }

  redeemPromotions = async (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => {
    await Promise.all(
      userPromotions.map((userPromotion) =>
        redeemPromotions(this.repository, {
          userId: userPromotion.data.userId,
          promotionId: userPromotion.data.promotionId,
          userPromotionId: userPromotion.id,
          sectionId,
        })
      )
    )
    return
  }

  applyApprovedFreeAdminPromotion = async ({
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }) => {
    return await applyApprovedFreeAdminPromotion(this.repository, {
      userId,
      sectionId,
    })
  }
}
